import type { CookSession, Recipe, RecipeInput, RecipeSummary } from "../../shared/recipes";

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  headers.set("accept", "application/json");
  if (init?.body !== undefined && !(init.body instanceof FormData)) {
    headers.set("content-type", "application/json");
  }
  return fetch(path, { ...init, headers });
}

async function readJsonResponse<T>(response: Response): Promise<T> {
  const body: unknown = await response.json();
  return body as T;
}

export async function fetchRecipes(): Promise<RecipeSummary[]> {
  const response = await apiFetch("/api/recipes");
  if (!response.ok) {
    throw new Error("recipes_fetch_failed");
  }
  const body = await readJsonResponse<{ recipes: RecipeSummary[] }>(response);
  return body.recipes;
}

export async function fetchRecipe(recipeId: string): Promise<Recipe> {
  const response = await apiFetch(`/api/recipes/${recipeId}`);
  if (!response.ok) {
    throw new Error("recipe_fetch_failed");
  }
  const body = await readJsonResponse<{ recipe: Recipe }>(response);
  return body.recipe;
}

export async function importRecipeFromUrl(url: string): Promise<RecipeInput> {
  const response = await apiFetch("/api/recipes/import-url", {
    method: "POST",
    body: JSON.stringify({ url })
  });
  if (!response.ok) {
    throw new Error("recipe_url_import_failed");
  }
  const body = await readJsonResponse<{ draft: RecipeInput }>(response);
  return body.draft;
}

export async function importRecipeJson(json: string): Promise<RecipeInput> {
  const response = await apiFetch("/api/recipes/import-json", {
    method: "POST",
    body: JSON.stringify({ json })
  });
  if (!response.ok) {
    throw new Error("recipe_json_import_failed");
  }
  const body = await readJsonResponse<{ draft: RecipeInput }>(response);
  return body.draft;
}

export async function importRecipeJsonBulk(json: string): Promise<RecipeInput[]> {
  const response = await apiFetch("/api/recipes/import-json-bulk", {
    method: "POST",
    body: JSON.stringify({ json })
  });
  if (!response.ok) {
    throw new Error("recipe_json_bulk_import_failed");
  }
  const body = await readJsonResponse<{ drafts: RecipeInput[] }>(response);
  return body.drafts;
}

export async function createRecipesBulk(recipes: readonly RecipeInput[]): Promise<Recipe[]> {
  const response = await apiFetch("/api/recipes/bulk", {
    method: "POST",
    body: JSON.stringify({ recipes })
  });
  if (!response.ok) {
    throw new Error("recipe_bulk_create_failed");
  }
  const body = await readJsonResponse<{ recipes: Recipe[] }>(response);
  return body.recipes;
}

export async function parseRecipePhotos(files: readonly File[]): Promise<RecipeInput> {
  const form = new FormData();
  for (const file of files) {
    form.append("images", file);
  }
  const response = await apiFetch("/api/recipes/parse-photo", { method: "POST", body: form });
  if (!response.ok) {
    throw new Error("recipe_photo_parse_failed");
  }
  const body = await readJsonResponse<{ draft: RecipeInput }>(response);
  return body.draft;
}

export async function parseRecipeDraft(text: string): Promise<RecipeInput> {
  const response = await apiFetch("/api/recipes/parse", {
    method: "POST",
    body: JSON.stringify({ text })
  });
  if (!response.ok) {
    throw new Error("recipe_parse_failed");
  }
  const body = await readJsonResponse<{ draft: RecipeInput }>(response);
  return body.draft;
}

export async function createRecipe(input: RecipeInput): Promise<Recipe> {
  const response = await apiFetch("/api/recipes", {
    method: "POST",
    body: JSON.stringify(input)
  });
  if (!response.ok) {
    throw new Error("recipe_create_failed");
  }
  const body = await readJsonResponse<{ recipe: Recipe }>(response);
  return body.recipe;
}

export async function updateRecipe(recipeId: string, input: RecipeInput): Promise<Recipe> {
  const response = await apiFetch(`/api/recipes/${recipeId}`, {
    method: "PUT",
    body: JSON.stringify(input)
  });
  if (!response.ok) {
    throw new Error("recipe_update_failed");
  }
  const body = await readJsonResponse<{ recipe: Recipe }>(response);
  return body.recipe;
}

export async function deleteRecipe(recipeId: string): Promise<void> {
  const response = await apiFetch(`/api/recipes/${recipeId}`, { method: "DELETE" });
  if (!response.ok) {
    throw new Error("recipe_delete_failed");
  }
}

export async function startCookSession(recipeId: string): Promise<{ session: CookSession; recipe: Recipe }> {
  const response = await apiFetch(`/api/recipes/${recipeId}/cook-sessions`, { method: "POST" });
  if (!response.ok) {
    throw new Error("cook_session_start_failed");
  }
  return readJsonResponse<{ session: CookSession; recipe: Recipe }>(response);
}

export async function fetchCookSession(sessionId: string): Promise<{ session: CookSession; recipe: Recipe }> {
  const response = await apiFetch(`/api/cook-sessions/${sessionId}`);
  if (!response.ok) {
    throw new Error("cook_session_fetch_failed");
  }
  return readJsonResponse<{ session: CookSession; recipe: Recipe }>(response);
}

export async function toggleCookStep(
  sessionId: string,
  stepId: string
): Promise<{ session: CookSession; recipe: Recipe }> {
  const response = await apiFetch(`/api/cook-sessions/${sessionId}/steps/${stepId}/toggle`, { method: "POST" });
  if (!response.ok) {
    throw new Error("cook_step_toggle_failed");
  }
  return readJsonResponse<{ session: CookSession; recipe: Recipe }>(response);
}

export async function toggleCookIngredient(
  sessionId: string,
  ingredientId: string
): Promise<{ session: CookSession; recipe: Recipe }> {
  const response = await apiFetch(`/api/cook-sessions/${sessionId}/ingredients/${ingredientId}/toggle`, {
    method: "POST"
  });
  if (!response.ok) {
    throw new Error("cook_ingredient_toggle_failed");
  }
  return readJsonResponse<{ session: CookSession; recipe: Recipe }>(response);
}

export type CookTimerAction = "started" | "completed" | "cancelled";

export async function recordCookTimerEvent(
  sessionId: string,
  action: CookTimerAction,
  payload: { timerId: string; stepId: string; label?: string; durationSeconds?: number }
): Promise<void> {
  const response = await apiFetch(`/api/cook-sessions/${sessionId}/timers/events`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ action, ...payload })
  });
  if (!response.ok) {
    throw new Error("cook_timer_event_failed");
  }
}
